// ADAPTED from ptc-core web/src/api/savedViews.ts (filter contract v3 §16.1-16.2, BINDING
// 2026-11-01, routes live since CRM deploy #22 — confirmed via `curl` returning 401, not 404, on
// both route families before writing this file). Same adaptation feedbackApi.ts already made:
// absolute CRM API URL + a dedicated MSAL scope (`crmApiRequest` in config/authConfig.ts) rather
// than this app's own services/api.ts client, which is scoped to OUR api's audience, not the
// CRM's. The DTOs and hook shapes mirror core's file exactly; only the HTTP plumbing differs.
//
// GET    /v1/me/screen-state/{app}/{screen}   — this person's remembered landing default (a query string, or null)
// PUT    /v1/me/screen-state/{app}/{screen}   — { stateQuery } ; replaces
// DELETE /v1/me/screen-state/{app}/{screen}   — "Reset to default"
// GET    /v1/saved-views?app=&screen=          — my views (any visibility) + the team's, and my default's id
// POST   /v1/saved-views                       — create (owner = the caller)
// PUT    /v1/saved-views/{id}                  — rename / re-save / re-share; owner or admin; omitted = unchanged
// DELETE /v1/saved-views/{id}                  — hard delete; every default on it goes too
// PUT    /v1/saved-views/default                — { app, screenKey, savedViewId | null } ; one per person per screen
//
// Both stores hold a URL QUERY STRING (no leading '?') and nothing else (§6.5: a view produces a
// URL, never a second shape of the state). `app` must be a HubAppCatalog id -- this app reports as
// 'tax-lifecycle' (the same literal feedbackApi.ts's FEEDBACK_APP_ID already uses).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../main';
import { crmApiRequest } from '../config/authConfig';

/** Fixed by the fleet contract -- not an env var, because it never varies by environment. Same
 * host feedbackApi.ts's CRM_FEEDBACK_ENDPOINT uses, minus the '/feedback' path. */
export const CRM_API_BASE = 'https://ptc-core-crm-api.azurewebsites.net/api/v1';

/** Exactly the id this app reports as to HubAppCatalog -- matches feedbackApi.ts's FEEDBACK_APP_ID. */
export const CRM_VIEWS_APP_ID = 'tax-lifecycle';

export interface ScreenStateDto {
  app: string;
  screenKey: string;
  /** The remembered query string, or null when nothing is remembered. */
  stateQuery: string | null;
  updatedAt: string | null;
}

export type SavedViewVisibility = 'personal' | 'team';

export interface SavedViewDto {
  id: string;
  app: string;
  screenKey: string;
  name: string;
  stateQuery: string;
  visibility: SavedViewVisibility;
  isOwner: boolean;
  /** Owner or admin: rename, re-save and delete are offered only when this is true. */
  canEdit: boolean;
  isDefault: boolean;
  ownerName: string | null;
  updatedAt: string;
}

export interface SavedViewsResponse {
  items: SavedViewDto[];
  defaultId: string | null;
}

export interface CreateSavedViewBody {
  app: string;
  screenKey: string;
  name: string;
  stateQuery: string;
  visibility?: SavedViewVisibility;
}

export type UpdateSavedViewBody = Partial<Pick<CreateSavedViewBody, 'name' | 'stateQuery' | 'visibility'>>;

export const crmViewKeys = {
  screenState: (app: string, screen: string) => ['crm-screen-state', app, screen] as const,
  all: ['crm-saved-views'] as const,
  forScreen: (app: string, screen: string) => ['crm-saved-views', app, screen] as const,
};

async function getCrmApiToken(): Promise<string> {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error('Not signed in');

  try {
    const response = await msalInstance.acquireTokenSilent({ ...crmApiRequest, account });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      throw new Error('Signing in again is required');
    }
    throw error;
  }
}

/** Shared request plumbing: auth header, JSON body, and a uniform error on any non-2xx. */
async function crmFetch<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const token = await getCrmApiToken();
  const response = await fetch(`${CRM_API_BASE}${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const detail = payload?.errors ? JSON.stringify(payload.errors) : response.statusText;
    throw new Error(`CRM views request failed (${response.status}): ${detail}`);
  }
  if (response.status === 200 || response.status === 201) {
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
  return undefined as T;
}

const screenStatePath = (app: string, screen: string) =>
  `/me/screen-state/${encodeURIComponent(app)}/${encodeURIComponent(screen)}`;

export function useScreenState(app: string, screen: string, enabled = true) {
  return useQuery({
    queryKey: crmViewKeys.screenState(app, screen),
    queryFn: () => crmFetch<ScreenStateDto>(screenStatePath(app, screen)),
    staleTime: Infinity,
    enabled,
  });
}

export function usePutScreenState(app: string, screen: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stateQuery: string) =>
      crmFetch<ScreenStateDto>(screenStatePath(app, screen), { method: 'PUT', body: { stateQuery } }),
    onSuccess: (saved) => {
      if (saved) queryClient.setQueryData(crmViewKeys.screenState(app, screen), saved);
    },
  });
}

export function useDeleteScreenState(app: string, screen: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => crmFetch<void>(screenStatePath(app, screen), { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.setQueryData<ScreenStateDto>(crmViewKeys.screenState(app, screen), {
        app, screenKey: screen, stateQuery: null, updatedAt: null,
      });
    },
  });
}

export function useSavedViews(app: string, screen: string) {
  return useQuery({
    queryKey: crmViewKeys.forScreen(app, screen),
    queryFn: () =>
      crmFetch<SavedViewsResponse>(`/saved-views?app=${encodeURIComponent(app)}&screen=${encodeURIComponent(screen)}`),
  });
}

function useInvalidatingMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: crmViewKeys.all });
    },
  });
}

export function useCreateSavedView() {
  return useInvalidatingMutation((body: CreateSavedViewBody) =>
    crmFetch<SavedViewDto>('/saved-views', { method: 'POST', body }));
}

export function useUpdateSavedView() {
  return useInvalidatingMutation(({ id, body }: { id: string; body: UpdateSavedViewBody }) =>
    crmFetch<SavedViewDto>(`/saved-views/${id}`, { method: 'PUT', body }));
}

export function useDeleteSavedView() {
  return useInvalidatingMutation((id: string) => crmFetch<void>(`/saved-views/${id}`, { method: 'DELETE' }));
}

/** Make a view this person's default for its screen (replacing the previous one), or clear it with null. */
export function useSetSavedViewDefault() {
  return useInvalidatingMutation(
    ({ app, screenKey, savedViewId }: { app: string; screenKey: string; savedViewId: string | null }) =>
      crmFetch<void>('/saved-views/default', { method: 'PUT', body: { app, screenKey, savedViewId } }));
}
